import {
  STEP_UP_EVENT_TYPE,
  StepUpEvent,
  StepUpInteractiveHost,
} from '@wix/stepup';
import { isEnforcementPostLoginPath, isPasskeyPostLoginPath } from '../routes';
import { PanoramaService } from '../services/panorama';

export const POST_LOGIN_STEP_UP_TRANSACTION = 'post-login-step-up';

export const STEP_UP_FLOW = {
  PASSKEY: 'passkey',
  TWO_FA_ENFORCEMENT: '2fa_enforcement',
  OTHER: 'other',
} as const;

const SUCCEEDED = 'succeeded';

interface StepUpMonitoringDeps {
  getPanorama: () => PanoramaService;
  getUserId: () => string | undefined;
  getPathname: () => string;
}

/**
 * One Panorama transaction per step-up, finished with whether the silent attempt
 * was enough (`screenShown: false`) or the step-up screen had to open.
 */
export class StepUpMonitoring {
  private screenShown = false;

  constructor(private readonly deps: StepUpMonitoringDeps) {}

  onEvent = (event: StepUpEvent): void => {
    const panorama = this.deps.getPanorama();
    switch (event.type) {
      case STEP_UP_EVENT_TYPE.MINT_STARTED:
        this.screenShown = false;
        panorama.startTransaction(POST_LOGIN_STEP_UP_TRANSACTION);
        return;
      case STEP_UP_EVENT_TYPE.MINT_SUCCEEDED:
        this.finish(SUCCEEDED);
        return;
      case STEP_UP_EVENT_TYPE.MINT_FAILED:
        this.finish(event.reason);
        return;
    }
  };

  // The step-up screen opens only when the silent attempt was not enough.
  withScreenTracking(host: StepUpInteractiveHost): StepUpInteractiveHost {
    return {
      open: (url, onCancel) => {
        this.screenShown = true;
        host.open(url, onCancel);
      },
      close: () => host.close(),
    };
  }

  private finish(outcome: string) {
    this.deps.getPanorama().finishTransaction(POST_LOGIN_STEP_UP_TRANSACTION, {
      customParams: {
        flow: this.flow(),
        screenShown: this.screenShown,
        outcome,
        userId: this.deps.getUserId() ?? '',
      },
    });
  }

  private flow() {
    const pathname = this.deps.getPathname();
    if (isPasskeyPostLoginPath(pathname)) {
      return STEP_UP_FLOW.PASSKEY;
    }
    if (isEnforcementPostLoginPath(pathname)) {
      return STEP_UP_FLOW.TWO_FA_ENFORCEMENT;
    }
    return STEP_UP_FLOW.OTHER;
  }
}
